import React from "react";
import { Link } from "react-router-dom";
import { Bicycle, PersonSimpleRun, TennisBall, MapPin, Clock, Users, Lightning, Lightning as LightningBolt } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import ProBadge from "@/components/ProBadge";

const sportMeta = {
  cycling: { Icon: Bicycle, color: "#007AFF", label: "CYCLING" },
  running: { Icon: PersonSimpleRun, color: "#FF3B30", label: "RUNNING" },
  padel:   { Icon: TennisBall, color: "#34C759", label: "PADEL" },
};

export default function ActivityCard({ activity }) {
  const { t, lang } = useI18n();
  const meta = sportMeta[activity.sport] || sportMeta.cycling;
  const filled = (activity.participants?.length || 0);
  const max = activity.max_participants;
  const pct = Math.min(100, Math.round((filled / max) * 100));
  const isFull = filled >= max;
  const coverUrl = activity.cover_url
    ? (activity.cover_url.startsWith("http") ? activity.cover_url : `${process.env.REACT_APP_BACKEND_URL}${activity.cover_url}`)
    : null;

  const locale = lang === "da" ? "da-DK" : "en-US";
  const date = new Date(activity.starts_at);
  const dayStr = isNaN(date) ? "—" : date.toLocaleDateString(locale, { weekday: "short", month: "short", day: "numeric" });
  const timeStr = isNaN(date) ? "" : date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });

  return (
    <Link
      to={`/activity/${activity.activity_id}`}
      data-testid={`activity-card-${activity.activity_id}`}
      className={`tf-card block group relative overflow-hidden ${activity.boosted ? "ring-1 ring-[#FFCC00]/50" : ""}`}
    >
      {coverUrl && (
        <div className="relative h-32 -mx-px -mt-px overflow-hidden border-b border-[#262626]" data-testid="activity-cover">
          <img src={coverUrl} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/30 to-transparent pointer-events-none" />
        </div>
      )}
      <div className="p-6">
      {activity.boosted && (
        <div className="absolute top-0 right-0 px-2 py-0.5 bg-gradient-to-r from-[#FFCC00] to-[#FF9500] text-black text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 z-10" data-testid="boosted-ribbon">
          <LightningBolt weight="fill" size={10} /> Boosted
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${meta.color}1F`, border: `1px solid ${meta.color}` }}>
            <meta.Icon weight="duotone" size={22} color={meta.color} />
          </div>
          <div>
            <div className="label-overline flex items-center gap-2" style={{ color: meta.color }}>
              <span>{meta.label}</span>
              {activity.host_is_pro && <ProBadge size={10} />}
            </div>
            <div className="font-display text-2xl leading-none mt-1 group-hover:translate-x-0.5 transition-transform">{activity.title}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="label-overline">{t("common.skill")}</div>
          <div className="font-mono-tag text-xs mt-1 text-white">{activity.skill_level}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-6">
        {activity.sport === "padel" ? (
          <>
            <Stat
              icon={<MapPin size={14} weight="duotone" />}
              label={(t("common.venue") || "Venue").toUpperCase()}
              value={activity.start_point?.address ? activity.start_point.address.split(",")[0] : "—"}
            />
            <Stat
              icon={<Lightning size={14} weight="duotone" />}
              label={(t("common.skill") || "Level").toUpperCase()}
              value={activity.skill_level || "—"}
            />
            <Stat
              icon={<Users size={14} weight="duotone" />}
              label={(t("common.spots") || "Spots").toUpperCase()}
              value={`${filled}/${max}`}
            />
          </>
        ) : (
          <>
            <Stat icon={<MapPin size={14} weight="duotone" />} label={t("common.distance").toUpperCase()} value={activity.distance_km ? `${activity.distance_km} ${t("common.km")}` : "—"} />
            <Stat icon={<Clock size={14} weight="duotone" />} label={t("common.duration").toUpperCase()} value={activity.duration_min ? `${Math.round(activity.duration_min)} ${t("common.min")}` : "—"} />
            <Stat icon={<Lightning size={14} weight="duotone" />} label={t("common.pace").toUpperCase()} value={activity.pace || "—"} />
          </>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <Users size={14} weight="duotone" />
          <span className="font-mono-tag">{filled}/{max}</span>
          {activity.live_now && (
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/40 uppercase tracking-widest" data-testid="live-now-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#34C759] animate-pulse" />
              Live · {activity.live_count}
            </span>
          )}
          {isFull && !activity.live_now && <span className="ml-2 px-2 py-0.5 text-[10px] font-bold bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/40 uppercase tracking-widest">{t("common.full")}</span>}
        </div>
        <div className="text-right">
          <div className="font-mono-tag text-xs text-zinc-200">{dayStr}</div>
          <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">{timeStr}</div>
        </div>
      </div>

      <div className="mt-3 h-[3px] bg-[#262626] overflow-hidden">
        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: meta.color }} />
      </div>
      </div>
    </Link>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="border-l border-[#262626] pl-3">
      <div className="flex items-center gap-1 text-zinc-500">{icon}<span className="label-overline">{label}</span></div>
      <div className="font-display text-lg mt-1 leading-none">{value}</div>
    </div>
  );
}
